import { format } from 'date-fns';

import { ProgramId } from 'modules/program';

import { IDestination, ILob, ISearch, searchWidgetConfig } from 'features/search-widget';

interface IGetSearchConfigOptions {
  programId: ProgramId;
}

function getSearchConfig({ programId }: IGetSearchConfigOptions): ISearch {
  const { search } = searchWidgetConfig[programId];

  return search;
}

interface IGetSearchUrlOptions {
  search: ISearch;
  selectedLob: ILob;
  fromDestination?: IDestination;
  toDestination?: IDestination;
  startDate?: Date;
  endDate?: Date;
}

function getSearchUrl({
  search,
  selectedLob,
  fromDestination,
  toDestination,
  startDate,
  endDate,
}: IGetSearchUrlOptions): string {
  const searchOrigin = `https://${search.hostname}`;

  let searchLocation = search.pathname ?? '/';
  let searchParams = new URLSearchParams({
    ...search.searchParams,
  });

  if (
    (selectedLob.id === 'stays' && toDestination) ||
    (selectedLob.id === 'flights' && fromDestination && toDestination && startDate && endDate)
  ) {
    searchLocation = `${search.pathname ?? ''}${search[selectedLob.id].pathname}`;
    searchParams = new URLSearchParams({
      ...(selectedLob.id === 'stays' && {
        destination: toDestination.data?.regionNames.shortName ?? toDestination.term,
        ...(startDate &&
          endDate && {
            // Dated
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
            d1: format(startDate, 'yyyy-MM-dd'),
            d2: format(endDate, 'yyyy-MM-dd'),
          }),
        rooms: '1',
        adults: '2',
        ...(toDestination.data?.regionId && {
          // Area
          regionId: toDestination.data.regionId,
        }),
        ...(toDestination.data?.cityId && {
          // Place
          regionId: toDestination.data.cityId,
          selected: toDestination.data.selected,
        }),
        ...(toDestination.data?.location && {
          latLong: [toDestination.data.location.lat, toDestination.data.location.long].join(),
        }),
      }),

      ...(selectedLob.id === 'flights' && {
        'flight-type': 'on',
        mode: 'search',
        trip: 'roundtrip',
        leg1: [
          `from:${fromDestination.data?.regionNames.shortName ?? fromDestination.term}`,
          `to:${toDestination.data?.regionNames.shortName ?? toDestination.term}`,
          `departure:${format(startDate, search.flights.dateFormat)}TANYT`,
        ].join(),
        leg2: [
          `from:${toDestination.data?.regionNames.shortName ?? toDestination.term}`,
          `to:${fromDestination.data?.regionNames.shortName ?? fromDestination.term}`,
          `departure:${format(endDate, search.flights.dateFormat)}TANYT`,
        ].join(),
        fromDate: format(startDate, search.flights.dateFormat),
        toDate: format(endDate, search.flights.dateFormat),
        d1: format(startDate, 'yyyy-MM-dd'),
        d2: format(endDate, 'yyyy-MM-dd'),
        passengers: ['children:0', 'adults:1', 'seniors:0', 'infantinlap:Y'].join(),
        options: 'cabinclass:economy',
      }),

      ...search.searchParams,
    });
  }

  const searchUrl = new URL(searchLocation, searchOrigin);

  for (const searchParam of searchParams) {
    searchUrl.searchParams.set(...searchParam);
  }

  return `${searchUrl}`;
}

export { getSearchConfig, getSearchUrl };
